import type { Lang, PageKey, SeoConfig } from "./types";

// =========================================================================
// PER-LANGUAGE SEO METADATA
// =========================================================================
//
// Canonical URLs are NOT stored here — they are derived from routes.ts via
// urlFor(page, lang) so there is exactly one place that defines a URL.
//
// The Spanish entries are not literal translations of the English titles.
// They target the phrases Spanish-speaking buyers actually type ("instalación
// de cámaras de seguridad Tampa", "soporte técnico en español"), which is the
// entire point of shipping an /es tree.

const en: Record<PageKey, SeoConfig> = {
  home: {
    title: "IT, Networks & Security for Tampa Bay Businesses | SightLine Networks",
    description:
      "SightLine Networks helps Tampa Bay businesses fix recurring IT problems, improve network reliability, eliminate Wi-Fi dead zones, install structured cabling and deploy professional security cameras. Free assessment.",
    ogTitle: "Reliable IT, Networks & Security for Tampa Bay Businesses",
    ogDescription:
      "Managed IT, business Wi-Fi, UniFi networks, security cameras and structured cabling for businesses in Clearwater, Tampa and St. Petersburg. Free assessment.",
    keywords:
      "managed IT Tampa, IT support Tampa Bay, business Wi-Fi installation Tampa, UniFi installer Tampa, security camera installation Tampa, structured cabling Tampa, network installation Clearwater, IT company Clearwater",
  },
  services: {
    title:
      "Business IT Services in Tampa Bay | Managed IT, Networks, Security & Web | SightLine Networks",
    description:
      "Explore IT services from SightLine Networks: managed IT support, network installation, security camera systems, server infrastructure, website development, and business web hosting across Tampa Bay.",
    ogTitle: "Business IT Services | SightLine Networks",
    ogDescription:
      "Managed IT, network installation, security cameras, server support, website development, and hosting for Tampa Bay businesses.",
  },
  hosting: {
    title:
      "Business Web Hosting Plans | Managed Hosting with SSL & Backups | SightLine Networks",
    description:
      "Reliable managed web hosting for business websites with free SSL, daily backups, uptime monitoring, and expert support. Plans start at $29/mo. Hosted and supported locally in Tampa Bay.",
    ogTitle: "Managed Business Web Hosting | SightLine Networks",
    ogDescription:
      "Fast, secure hosting plans for business websites with SSL, daily backups, monitoring, and Tampa Bay-based support.",
  },
  solutions: {
    title:
      "Business Technology Solutions | Office IT, Wi-Fi, Security & Websites | SightLine Networks",
    description:
      "Find the right technology solution for your business. From office IT setup and Wi-Fi optimization to security cameras, web hosting, and ongoing support across Tampa Bay.",
    ogTitle: "Business Technology Solutions | SightLine Networks",
    ogDescription:
      "Problem-focused technology solutions for Tampa Bay businesses: office setup, networking, security, hosting, and websites.",
  },
  areas: {
    title:
      "IT Services in Tampa, Clearwater, Largo, St. Petersburg & Tampa Bay | SightLine Networks",
    description:
      "SightLine Networks provides managed IT, web hosting, network installation, security cameras, and website development to businesses in Tampa, Clearwater, Largo, St. Petersburg, and across Tampa Bay.",
    ogTitle: "Tampa Bay IT Services Coverage | SightLine Networks",
    ogDescription:
      "Local IT services, hosting, networking, security, and web solutions across the Tampa Bay area including Tampa, Clearwater, and St. Petersburg.",
  },
  about: {
    title: "About SightLine Networks | Your Tampa Bay IT & Hosting Partner",
    description:
      "SightLine Networks is a Tampa Bay technology company helping businesses with managed IT, web hosting, network infrastructure, security cameras, and website development since day one.",
    ogTitle: "About SightLine Networks",
    ogDescription:
      "A trusted technology partner for Tampa Bay businesses needing IT support, hosting, networking, security, and web solutions.",
  },
  contact: {
    title: "Contact SightLine Networks | Get a Free IT Services Quote",
    description:
      "Contact SightLine Networks for a free quote on managed IT, web hosting, network installation, security cameras, or website development. Serving Tampa Bay businesses. Call (727) 283-6452.",
    ogTitle: "Contact SightLine Networks | Get a Quote",
    ogDescription:
      "Request a free quote for managed IT, hosting, websites, networking, or security camera projects in Tampa Bay.",
  },
  "managed-it": {
    title:
      "Managed IT Services in Tampa Bay | Ubiquiti, Cisco & Synology Support | SightLine Networks",
    description:
      "Proactive managed IT for Tampa Bay businesses. 24/7 monitoring, helpdesk, patch management, cybersecurity, and ongoing support for Ubiquiti UniFi, Cisco, and Synology environments. Free IT assessment.",
    ogTitle: "Managed IT Services | SightLine Networks",
    ogDescription:
      "24/7 managed IT support for Ubiquiti, Cisco, and Synology environments in Tampa Bay.",
    keywords:
      "managed IT Tampa Bay, IT support Tampa, Ubiquiti UniFi support, Cisco support, Synology support, helpdesk Tampa, business IT monitoring, cybersecurity Tampa, patch management",
  },
  // Dedicated Google Ads landing page — keep title/description tightly matched
  // to the ad groups it serves (see the keywords list) rather than the broader
  // "managed-it" service page above.
  "managed-it-services": {
    title: "Managed IT Services Tampa Bay | SightLine Networks",
    description:
      "Business IT support, Microsoft 365, cybersecurity, network management and managed IT services for Tampa Bay businesses. Schedule a free IT consultation with SightLine Networks.",
    ogTitle: "Managed IT Services in Tampa Bay | SightLine Networks",
    ogDescription:
      "Business IT support, Microsoft 365 management, cybersecurity, and network management for Tampa Bay businesses. Schedule a free IT consultation.",
    keywords:
      "managed IT services, managed IT services Tampa, managed IT services Clearwater, IT support Tampa, business IT support, IT support company near me, outsourced IT services, managed service provider Tampa Bay, Microsoft 365 support for business, business network support",
  },
  "network-installation": {
    title:
      "Ubiquiti UniFi & Cisco Network Installation in Tampa Bay | Wi-Fi, Switches & Cabling | SightLine Networks",
    description:
      "Professional Ubiquiti UniFi and Cisco network installation for Tampa Bay businesses. UniFi Network design, UniFi access points, Cisco/Meraki switches, structured cabling, firewalls, and enterprise Wi-Fi deployment.",
    ogTitle:
      "Ubiquiti UniFi & Cisco Network Installation | SightLine Networks",
    ogDescription:
      "Certified UniFi and Cisco installers in Tampa Bay. Enterprise Wi-Fi, switches, firewalls, and structured cabling for businesses.",
    keywords:
      "Ubiquiti UniFi installer Tampa, UniFi Network, UniFi access points, Cisco networking, Meraki, network installation Tampa Bay, business Wi-Fi installation, structured cabling Tampa, enterprise Wi-Fi, network switch installation, firewall installation",
  },
  "security-cameras": {
    title:
      "UniFi Protect, Hikvision & Axis Security Camera Installation in Tampa Bay | SightLine Networks",
    description:
      "Commercial IP security cameras for Tampa Bay businesses. Certified installers of Ubiquiti UniFi Protect, Hikvision, Axis, and Dahua cameras with 4K video, remote viewing, NVR/cloud recording, and access control.",
    ogTitle: "UniFi Protect & IP Camera Installation | SightLine Networks",
    ogDescription:
      "Professional IP security camera systems from Ubiquiti UniFi Protect, Hikvision, Axis, and Dahua for Tampa Bay businesses.",
    keywords:
      "UniFi Protect installer, Ubiquiti security cameras, IP camera installation Tampa, Hikvision installer, Axis Communications cameras, Dahua, Amcrest, Reolink, commercial security cameras Tampa Bay, business surveillance, 4K IP cameras, NVR installation, access control Tampa",
  },
  "server-cloud": {
    title:
      "Synology NAS, Server & Cloud Infrastructure in Tampa Bay | Local Backup & Migration | SightLine Networks",
    description:
      "Synology NAS installation, server deployment, cloud migration, and disaster recovery for Tampa Bay businesses. Local backup, hybrid infrastructure, virtualization, and Synology DSM support.",
    ogTitle: "Synology NAS & Server Installation | SightLine Networks",
    ogDescription:
      "Synology NAS, on-premise servers, local backup, cloud migration, and hybrid infrastructure for Tampa Bay businesses.",
    keywords:
      "Synology installer Tampa, Synology NAS, Synology DSM, local backup Tampa Bay, business backup, server installation Tampa, on-premise server, cloud migration, disaster recovery, virtualization, hybrid cloud, business storage",
  },
  "website-development": {
    title:
      "Website Development in Tampa Bay | Business Websites & SEO | SightLine Networks",
    description:
      "Professional website development for Tampa Bay businesses. Responsive design, SEO optimization, lead generation, CMS management, and ongoing support. Pair with managed hosting.",
    ogTitle: "Website Development | SightLine Networks",
    ogDescription:
      "Modern business websites with responsive design, SEO, lead generation, and ongoing support in Tampa Bay.",
  },
  "it-consulting": {
    title:
      "IT Consulting & Strategy in Tampa Bay | Technology Assessments | SightLine Networks",
    description:
      "Strategic IT consulting for Tampa Bay businesses. Technology assessments, infrastructure planning, compliance guidance, vendor evaluation, and IT roadmap development.",
    ogTitle: "IT Consulting & Strategy | SightLine Networks",
    ogDescription:
      "Technology assessments, IT planning, compliance guidance, and strategic consulting for Tampa Bay businesses.",
  },
  privacy: {
    title: "Privacy Policy | SightLine Networks",
    description:
      "Learn how SightLine Networks collects, uses, and shares personal information, plus your California privacy rights under the CCPA/CPRA, including how to opt out of the sale or sharing of personal information.",
    ogTitle: "Privacy Policy | SightLine Networks",
    ogDescription:
      "How SightLine Networks handles your personal information and your California privacy rights under the CCPA/CPRA.",
  },
  // Conversion-focused Google Ads / high-intent organic landing pages.
  // Each title/description message-matches one ad group; see the matching
  // landing component in src/landing/.
  "business-wifi": {
    title: "Business Wi-Fi Installation Tampa | Commercial Wi-Fi | SightLine Networks",
    description:
      "Slow or unreliable business Wi-Fi in Tampa Bay? SightLine Networks designs, installs and troubleshoots professional networks for offices, warehouses and commercial facilities. Request a Wi-Fi assessment.",
    ogTitle: "Business Wi-Fi Installation in Tampa Bay | SightLine Networks",
    ogDescription:
      "Fix Wi-Fi dead zones, dropped connections and slow speeds. Professional business Wi-Fi installation and troubleshooting for Tampa Bay businesses.",
    keywords:
      "business Wi-Fi installation Tampa, commercial Wi-Fi installer Tampa Bay, Wi-Fi dead zones office, warehouse Wi-Fi Tampa, access point installation Tampa, enterprise Wi-Fi Clearwater, fix business Wi-Fi, wireless network installation St. Petersburg",
  },
  "unifi-installer": {
    title: "UniFi Installer Tampa Bay | Ubiquiti UniFi Setup & Support | SightLine Networks",
    description:
      "Independent Ubiquiti UniFi installation and support in Tampa Bay. Access points, switches, gateways, UniFi Protect cameras, VLANs and guest Wi-Fi for businesses. New installs, upgrades and troubleshooting.",
    ogTitle: "UniFi Installer in Tampa Bay | Ubiquiti UniFi for Business",
    ogDescription:
      "Professional Ubiquiti UniFi installation and support for Tampa Bay businesses — access points, switching, gateways and UniFi Protect cameras.",
    keywords:
      "UniFi installer Tampa, Ubiquiti installer Tampa Bay, UniFi installation, Ubiquiti network setup, UniFi Protect installer, UniFi support Tampa, UniFi troubleshooting, business UniFi network Clearwater",
  },
  "camera-installation": {
    title: "Security Camera Installation Tampa | Business Cameras | SightLine Networks",
    description:
      "Professional security camera installation for Tampa Bay businesses — offices, warehouses, retail and parking areas. IP camera systems from UniFi Protect, Hikvision, Axis and Dahua with remote viewing. Request a site survey.",
    ogTitle: "Business Security Camera Installation in Tampa Bay",
    ogDescription:
      "Network-based camera systems for Tampa Bay businesses — entry/exit coverage, parking areas, warehouses and remote viewing. Free site survey.",
    keywords:
      "security camera installation Tampa, business security cameras Clearwater, commercial security cameras Tampa Bay, warehouse cameras Tampa, UniFi Protect installer, IP camera installation, NVR installation Tampa, camera site survey",
  },
  "structured-cabling": {
    title: "Structured Cabling Tampa Bay | Cat6 Installation | SightLine Networks",
    description:
      "Professional network and structured cabling in Tampa Bay — Cat6/Cat6A drops, network racks, patch panels, access point and camera cabling, office buildouts and rack cleanups. Request a cabling estimate.",
    ogTitle: "Network & Structured Cabling in Tampa Bay | Cat6 Installation",
    ogDescription:
      "Cat6/Cat6A cabling, network racks and patch panels installed, tested and labeled for Tampa Bay offices, warehouses and buildouts.",
    keywords:
      "structured cabling Tampa, Cat6 installation Tampa, network cabling Clearwater, cable installation St. Petersburg, network rack installation, patch panel installation, office buildout cabling, low voltage cabling Tampa Bay",
  },
};

const es: Record<PageKey, SeoConfig> = {
  home: {
    title:
      "IT, Redes y Seguridad para Empresas en Tampa Bay | SightLine Networks",
    description:
      "SightLine Networks ayuda a empresas de Tampa Bay a resolver problemas de IT recurrentes, mejorar la confiabilidad de la red, eliminar zonas sin Wi-Fi, instalar cableado estructurado y desplegar cámaras de seguridad profesionales. Evaluación gratuita. Atención en español.",
    ogTitle: "IT, Redes y Seguridad Confiables para Empresas de Tampa Bay",
    ogDescription:
      "Soporte IT administrado, Wi-Fi empresarial, redes UniFi, cámaras de seguridad y cableado estructurado para empresas en Clearwater, Tampa y St. Petersburg. Atención en español.",
    keywords:
      "soporte IT en español Tampa, servicios de IT para empresas Tampa Bay, instalación de redes Tampa, instalador UniFi Tampa, cámaras de seguridad Tampa, cableado estructurado Tampa, wifi para empresas Clearwater, técnico de redes Tampa Bay",
  },
  services: {
    title:
      "Servicios de IT para Empresas en Tampa Bay | Soporte, Redes, Seguridad y Web | SightLine Networks",
    description:
      "Conozca los servicios de SightLine Networks: soporte IT administrado, instalación de redes, sistemas de cámaras de seguridad, infraestructura de servidores, desarrollo de sitios web y hosting empresarial en todo Tampa Bay. Atención en español.",
    ogTitle: "Servicios de IT para Empresas | SightLine Networks",
    ogDescription:
      "Soporte IT administrado, instalación de redes, cámaras de seguridad, servidores, desarrollo web y hosting para empresas de Tampa Bay.",
    keywords:
      "servicios de IT en español, soporte informático para empresas Tampa, instalación de redes empresariales, cámaras de seguridad comerciales, servicios tecnológicos Tampa Bay",
  },
  hosting: {
    title:
      "Planes de Hosting Web Empresarial | Hosting Administrado con SSL y Respaldos | SightLine Networks",
    description:
      "Hosting web administrado y confiable para sitios empresariales, con SSL gratuito, respaldos diarios, monitoreo de disponibilidad y soporte experto en español. Planes desde $29/mes, con soporte local en Tampa Bay.",
    ogTitle: "Hosting Web Empresarial Administrado | SightLine Networks",
    ogDescription:
      "Planes de hosting rápidos y seguros para sitios empresariales, con SSL, respaldos diarios, monitoreo y soporte en español desde Tampa Bay.",
    keywords:
      "hosting web en español, alojamiento web empresarial Tampa, hosting administrado, hosting con SSL gratis, respaldos diarios, hosting para negocios Florida",
  },
  solutions: {
    title:
      "Soluciones Tecnológicas para Empresas | IT de Oficina, Wi-Fi, Seguridad y Sitios Web | SightLine Networks",
    description:
      "Encuentre la solución tecnológica adecuada para su empresa. Desde la habilitación de IT en la oficina y la optimización del Wi-Fi hasta cámaras de seguridad, hosting web y soporte continuo en todo Tampa Bay.",
    ogTitle: "Soluciones Tecnológicas para Empresas | SightLine Networks",
    ogDescription:
      "Soluciones tecnológicas enfocadas en resolver problemas reales de empresas en Tampa Bay: oficinas, redes, seguridad, hosting y sitios web.",
    keywords:
      "soluciones tecnológicas para empresas, mejorar wifi oficina, habilitar nueva oficina Tampa, soporte IT en español",
  },
  areas: {
    title:
      "Servicios de IT en Tampa, Clearwater, Largo, St. Petersburg y Tampa Bay | SightLine Networks",
    description:
      "SightLine Networks brinda soporte IT administrado, hosting web, instalación de redes, cámaras de seguridad y desarrollo de sitios web a empresas de Tampa, Clearwater, Largo, St. Petersburg y toda el área de Tampa Bay. Atención en español.",
    ogTitle: "Cobertura de Servicios de IT en Tampa Bay | SightLine Networks",
    ogDescription:
      "Servicios locales de IT, hosting, redes, seguridad y soluciones web en toda el área de Tampa Bay, incluyendo Tampa, Clearwater y St. Petersburg.",
    keywords:
      "servicios de IT en español Tampa, técnico de computadoras Clearwater, instalación de redes Largo, cámaras de seguridad St. Petersburg, soporte IT Pinellas",
  },
  about: {
    title:
      "Sobre SightLine Networks | Su Socio de IT y Hosting en Tampa Bay",
    description:
      "SightLine Networks es una empresa de tecnología de Tampa Bay que ayuda a los negocios con soporte IT administrado, hosting web, infraestructura de redes, cámaras de seguridad y desarrollo de sitios web. Atención en español.",
    ogTitle: "Sobre SightLine Networks",
    ogDescription:
      "Un socio tecnológico confiable para empresas de Tampa Bay que necesitan soporte IT, hosting, redes, seguridad y soluciones web.",
  },
  contact: {
    title:
      "Contacte a SightLine Networks | Cotización Gratis de Servicios de IT",
    description:
      "Contacte a SightLine Networks para una cotización gratuita de soporte IT administrado, hosting web, instalación de redes, cámaras de seguridad o desarrollo de sitios web. Atendemos empresas de Tampa Bay en español. Llame al (727) 283-6452.",
    ogTitle: "Contacte a SightLine Networks | Solicite una Cotización",
    ogDescription:
      "Solicite una cotización gratuita de soporte IT, hosting, sitios web, redes o cámaras de seguridad en Tampa Bay. Hablamos español.",
    keywords:
      "cotización servicios de IT en español, contactar técnico de redes Tampa, presupuesto cámaras de seguridad Tampa",
  },
  "managed-it": {
    title:
      "Soporte IT Administrado en Tampa Bay | Servicio para Ubiquiti, Cisco y Synology | SightLine Networks",
    description:
      "Soporte IT administrado y proactivo para empresas de Tampa Bay. Monitoreo 24/7, helpdesk, gestión de parches, ciberseguridad y soporte continuo para entornos Ubiquiti UniFi, Cisco y Synology. Evaluación gratuita y atención en español.",
    ogTitle: "Soporte IT Administrado | SightLine Networks",
    ogDescription:
      "Soporte IT administrado 24/7 para entornos Ubiquiti, Cisco y Synology en Tampa Bay, con atención en español.",
    keywords:
      "soporte IT en español Tampa, soporte técnico administrado, mantenimiento informático para empresas, helpdesk en español, ciberseguridad para negocios Tampa, monitoreo de sistemas 24/7",
  },
  "managed-it-services": {
    title: "Servicios de TI Administrados en Tampa Bay | SightLine Networks",
    description:
      "Soporte de TI para empresas, Microsoft 365, ciberseguridad y administración de redes para negocios de Tampa Bay. Programe una consulta de TI gratuita con SightLine Networks.",
    ogTitle: "Servicios de TI Administrados en Tampa Bay | SightLine Networks",
    ogDescription:
      "Soporte de TI, administración de Microsoft 365, ciberseguridad y redes para empresas de Tampa Bay. Consulta de TI gratuita.",
    keywords:
      "servicios de TI administrados Tampa, soporte de TI para empresas, proveedor de servicios administrados Tampa Bay, soporte técnico empresarial Tampa, Microsoft 365 para empresas",
  },
  "network-installation": {
    title:
      "Instalación de Redes Ubiquiti UniFi y Cisco en Tampa Bay | Wi-Fi, Switches y Cableado | SightLine Networks",
    description:
      "Instalación profesional de redes Ubiquiti UniFi y Cisco para empresas de Tampa Bay. Diseño de UniFi Network, access points UniFi, switches Cisco/Meraki, cableado estructurado, firewalls y despliegue de Wi-Fi empresarial. Atención en español.",
    ogTitle:
      "Instalación de Redes Ubiquiti UniFi y Cisco | SightLine Networks",
    ogDescription:
      "Instaladores certificados de UniFi y Cisco en Tampa Bay. Wi-Fi empresarial, switches, firewalls y cableado estructurado para negocios.",
    keywords:
      "instalación de redes Tampa, cableado estructurado Tampa, instalador Ubiquiti UniFi, redes Cisco Meraki, wifi empresarial Tampa Bay, técnico de redes en español, instalación de wifi para negocios, configuración de firewall",
  },
  "security-cameras": {
    title:
      "Instalación de Cámaras de Seguridad UniFi Protect, Hikvision y Axis en Tampa Bay | SightLine Networks",
    description:
      "Cámaras de seguridad IP comerciales para empresas de Tampa Bay. Instaladores certificados de Ubiquiti UniFi Protect, Hikvision, Axis y Dahua, con video 4K, visualización remota, grabación en NVR o nube y control de acceso. Atención en español.",
    ogTitle:
      "Instalación de Cámaras UniFi Protect y Cámaras IP | SightLine Networks",
    ogDescription:
      "Sistemas profesionales de cámaras de seguridad IP de Ubiquiti UniFi Protect, Hikvision, Axis y Dahua para empresas de Tampa Bay.",
    keywords:
      "instalación de cámaras de seguridad Tampa, cámaras de seguridad para negocios, camaras de vigilancia Tampa Bay, instalador de cámaras IP en español, Hikvision Tampa, UniFi Protect, cámaras 4K comerciales, sistema de videovigilancia, control de acceso Tampa",
  },
  "server-cloud": {
    title:
      "Synology NAS, Servidores e Infraestructura en la Nube en Tampa Bay | Respaldo Local y Migración | SightLine Networks",
    description:
      "Instalación de Synology NAS, despliegue de servidores, migración a la nube y recuperación ante desastres para empresas de Tampa Bay. Respaldo local, infraestructura híbrida, virtualización y soporte de Synology DSM en español.",
    ogTitle: "Synology NAS e Instalación de Servidores | SightLine Networks",
    ogDescription:
      "Synology NAS, servidores locales, respaldo, migración a la nube e infraestructura híbrida para empresas de Tampa Bay.",
    keywords:
      "Synology NAS Tampa, instalación de servidores Tampa, respaldo de datos para empresas, migración a la nube en español, recuperación ante desastres, almacenamiento empresarial, virtualización VMware",
  },
  "website-development": {
    title:
      "Desarrollo de Páginas Web en Tampa Bay | Sitios Empresariales y SEO | SightLine Networks",
    description:
      "Desarrollo profesional de páginas web para empresas de Tampa Bay. Diseño adaptable, optimización SEO, generación de prospectos, gestión de CMS y soporte continuo. Combínelo con hosting administrado. Atención en español.",
    ogTitle: "Desarrollo de Páginas Web | SightLine Networks",
    ogDescription:
      "Sitios web empresariales modernos con diseño adaptable, SEO, generación de prospectos y soporte continuo en Tampa Bay.",
    keywords:
      "diseño de páginas web Tampa, desarrollo web en español, crear pagina web para negocio, diseño web empresarial Florida, SEO en español, paginas web para empresas Tampa Bay",
  },
  "it-consulting": {
    title:
      "Consultoría y Estrategia de IT en Tampa Bay | Evaluaciones Tecnológicas | SightLine Networks",
    description:
      "Consultoría estratégica de IT para empresas de Tampa Bay. Evaluaciones tecnológicas, planificación de infraestructura, orientación en cumplimiento normativo, evaluación de proveedores y hojas de ruta de IT. Atención en español.",
    ogTitle: "Consultoría y Estrategia de IT | SightLine Networks",
    ogDescription:
      "Evaluaciones tecnológicas, planificación de IT, orientación en cumplimiento y consultoría estratégica para empresas de Tampa Bay.",
    keywords:
      "consultoría de IT en español, asesoría tecnológica para empresas, evaluación tecnológica Tampa, cumplimiento HIPAA PCI, planificación de infraestructura IT",
  },
  privacy: {
    title: "Política de Privacidad | SightLine Networks",
    description:
      "Conozca cómo SightLine Networks recopila, usa y comparte información personal, además de sus derechos de privacidad en California conforme a la CCPA/CPRA, incluida la forma de excluirse de la venta o el intercambio de información personal.",
    ogTitle: "Política de Privacidad | SightLine Networks",
    ogDescription:
      "Cómo SightLine Networks maneja su información personal y sus derechos de privacidad en California conforme a la CCPA/CPRA.",
  },
  // Páginas de aterrizaje de alta intención (Google Ads + orgánico). Los
  // títulos apuntan a las frases que buscan los compradores en español.
  "business-wifi": {
    title: "Wi-Fi Empresarial Tampa | Instalación de Wi-Fi para Negocios | SightLine Networks",
    description:
      "¿Wi-Fi lento o inestable en su empresa de Tampa Bay? SightLine Networks diseña, instala y corrige redes profesionales para oficinas, bodegas y locales comerciales. Solicite una evaluación de Wi-Fi.",
    ogTitle: "Instalación de Wi-Fi Empresarial en Tampa Bay | SightLine Networks",
    ogDescription:
      "Elimine zonas sin cobertura, caídas de conexión y velocidad lenta. Instalación profesional de Wi-Fi empresarial y corrección de redes en Tampa Bay.",
    keywords:
      "wifi para empresas Tampa, instalación de wifi empresarial Tampa Bay, wifi para bodegas Tampa, puntos de acceso Tampa, mejorar wifi oficina, redes inalámbricas empresariales, técnico wifi Clearwater, problemas de wifi negocio",
  },
  "unifi-installer": {
    title: "Instalador UniFi Tampa Bay | Instalación y Soporte Ubiquiti | SightLine Networks",
    description:
      "Instalación y soporte independiente de Ubiquiti UniFi en Tampa Bay. Access points, switches, gateways, cámaras UniFi Protect, VLANs y Wi-Fi para invitados. Instalaciones nuevas, actualizaciones y corrección de redes.",
    ogTitle: "Instalador UniFi en Tampa Bay | Ubiquiti UniFi para Empresas",
    ogDescription:
      "Instalación profesional de redes Ubiquiti UniFi para empresas de Tampa Bay — access points, switches, gateways y cámaras UniFi Protect.",
    keywords:
      "instalador UniFi Tampa, instalación Ubiquiti Tampa Bay, redes Ubiquiti para empresas, UniFi Protect instalador, soporte UniFi Tampa, configuración UniFi, técnico Ubiquiti Tampa Bay",
  },
  "camera-installation": {
    title: "Instalación de Cámaras de Seguridad Tampa | Cámaras para Negocios | SightLine Networks",
    description:
      "Instalación profesional de cámaras de seguridad para empresas de Tampa Bay — oficinas, bodegas, comercio y estacionamientos. Sistemas IP de UniFi Protect, Hikvision, Axis y Dahua con visualización remota. Solicite una visita técnica.",
    ogTitle: "Instalación de Cámaras de Seguridad para Empresas en Tampa Bay",
    ogDescription:
      "Cámaras IP para empresas de Tampa Bay — cobertura de entradas, estacionamientos, bodegas y visualización remota. Visita técnica gratuita.",
    keywords:
      "instalación de cámaras de seguridad Tampa, cámaras de seguridad para negocios Tampa Bay, cámaras para bodegas Tampa, instalador de cámaras IP Clearwater, UniFi Protect, sistema de videovigilancia Tampa, cámaras de seguridad St. Petersburg",
  },
  "structured-cabling": {
    title: "Cableado Estructurado Tampa Bay | Instalación Cat6 | SightLine Networks",
    description:
      "Cableado estructurado y de red profesional en Tampa Bay — canales Cat6/Cat6A, racks de red, patch panels, cableado para access points y cámaras, remodelaciones de oficinas y orden de racks. Solicite un presupuesto de cableado.",
    ogTitle: "Cableado Estructurado y de Red en Tampa Bay | Instalación Cat6",
    ogDescription:
      "Cableado Cat6/Cat6A, racks de red y patch panels instalados, probados y etiquetados para oficinas y bodegas de Tampa Bay.",
    keywords:
      "cableado estructurado Tampa, instalación Cat6 Tampa, cableado de red Clearwater, técnico de cableado Tampa Bay, rack de red, patch panel, cableado para cámaras Tampa, cableado para oficinas",
  },
};

export const SEO_CONFIG: Record<Lang, Record<PageKey, SeoConfig>> = { en, es };
